import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { PATH_PAGE_ITERATION_SYMBOL } from '@workflow/class/stateful-value.class';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { LogicFnContext } from '@workflow/service/logic/logic.service';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {
  // get bool result if value exists.
  // bool values will always be true.
  public hasValue(value): boolean {
    return (
      value !== null &&
      value.toString() !== 'null' &&
      value !== undefined &&
      value.toString() !== 'undefined' &&
      value.toString().trim().length > 0
    );
  }
}

// TODO: Why aren't these methods just methods in the service? Why have them standalone?

/**
 * Replace page iterative literal with numeric index
 * @param configId storageId path with or without iterative tokens #
 * @param arg1 can be replacement index or logic fn context
 */

export const processPath = (configId: string, arg1?: number | LogicFnContext): string => {
  if (configId.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
    return configId;
  }
  const pageIterationId = typeof arg1 === 'number' ? arg1 : getPageIterationId(arg1);
  if (pageIterationId == null) {
    throw new Error(`Unable to determine iterationId for config: ${configId}.`);
  }
  return configId.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
};

/**
 * Method to check for any invalid fields
 * if any requireds fileds invalid it will force to ediit mode in prefill
 */
export const validatePageOnPrefill = (
  statefulService: StatefulService,
  context: LogicFnContext,
  location: PlatformLocation,
  previewCardId: string,
  isStreamlined: boolean = false
): void => {

  // This checks if any card on a page has tcpa checkbox that is visible. 
  // If it does, it will not show the condensed view
  const isTcpaFieldVisible = context.page?.cards?.some(card => 
    card.fields?.some(field => 
      field.id?.includes('tcpa') && field.visible === true
    )
  ) || false;

  if (location.hash?.length || isStreamlined || isTcpaFieldVisible) {
    statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
    return;
  }
  if (
    context.page.cards.filter((t) => t.visible && t.helper.flatVisibleFields.findIndex((f) => f.required && !f.valid) !== -1).length !== 0
  ) {
    statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
  } else {
    statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = false;
  }
};

/**
 * Check and returns empty string for primary and interpreted prefix with numeric index i for co-borrower 'coBorrower/i/'
 * @param context context for path interpretation
 */
export const coAppSwitch = (context: LogicFnContext): string => {
  const ref = context.card || context.field;
  return ref?.config?.id.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1 ? processPath('coBorrowers/#/', context) : '';
};

/**
 * Replace # in config id with pageIterationId
 */
export const getPageIterationId = (context: LogicFnContext): number => {
  const ref = context && (context.field || context.card || context.page);
  return ref?.pageIterationId;
};

/**
 * Replace $ in config id with IterationId
 */
export const getIterationId = (context: LogicFnContext): number => {
  const ref = context && (context.field || context.card);
  return ref?.iterationId;
};

export const getRouteConfig = (config: Routes, path: string, prefix = ''): Route[] => {
  let routeConfig = config.filter((t) => t.path.includes(path));
  prefix = prefix?.endsWith('/') ? prefix.slice(0, prefix.lastIndexOf('/')) : prefix;
  // eslint-disable-next-line eqeqeq
  const parent = config.filter((t) => t.path == prefix);
  if (parent.length && parent[0].children) {
    routeConfig = parent[0].children.filter((t) => t.path.includes(path));
  }
  return routeConfig;
};

export const getValue = (_context: LogicFnContext): PrimitiveType => {
  const data = _context.value || _context.field?.data;
  if (!data) {
    throw Error(`Validation '${_context.functionName}' used in a wrong context. It has to be used either in field or stateful context.`);
  }
  return data.value;
};
